import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import cx from 'classnames';
import ContentContainer from 'components/content-container';
import Hr from 'components/PRISMIC/Blog/Hr';

import { Text } from 'components/@tedui';
import LinkListComponent from './LinkListComponent';
import ListGridComponent from './ListGridComponent';

import type { JSX } from 'react';

/**
 * Props for `OurImpact`.
 */
export type OurImpactProps = SliceComponentProps<Content.OurImpactSlice>;

/**
 * Component for "OurImpact" Slices.
 */
const OurImpact = ({ slice }: OurImpactProps): JSX.Element => {
  const isDefaultVariation = slice.variation === 'default';
  const isGrayVariation = slice.variation === 'grayBackground';
  const isBlackVariation = slice.variation === 'blackBackground';
  const isListLinks = slice.variation === 'listLink';
  const isListLinksFormattedSideText =
    slice.variation === 'listLinkFormattedSideText';

  const textColor = cx({
    'text-white': isBlackVariation,
    'text-pureBlack': !isBlackVariation
  });

  const paddingBottomByVariation = cx(
    'mt-6 grid grid-cols-1 gap-4 md-tui:grid-cols-2 md-tui:gap-4 lg-tui:items-start lg-tui:gap-10',
    {
      'pb-16 md-tui:pb-20': !isListLinks
    }
  );

  if (slice.primary.is_hidden) {
    return null;
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={cx('pb-16 md-tui:pb-20', {
        'bg-white':
          isDefaultVariation ||
          (isListLinks && slice.primary.background === 'White'),
        'bg-gray-50':
          isGrayVariation ||
          isListLinksFormattedSideText ||
          (isListLinks && slice.primary.background === 'Default'),
        'bg-pureBlack': isBlackVariation
      })}
    >
      <ContentContainer
        className="mx-auto max-w-7xl px-5 text-white sm-tui:px-10"
        noPadding
      >
        <Hr className="bg-pureBlack" />

        <div className={paddingBottomByVariation}>
          <Text
            variant={!isListLinks ? 'header2' : 'header1'}
            UNSAFE_className={`flex-1 ${textColor}`}
            useNewTextStyles
          >
            {slice.primary.title}
          </Text>
          {isListLinksFormattedSideText ? (
            <div className="flex flex-col gap-4 pb-6">
              {slice.primary.side_text.map(item => (
                <Text
                  variant="header4"
                  UNSAFE_className={`flex-1 ${textColor}`}
                  useNewTextStyles
                >
                  {item.text}
                </Text>
              ))}
            </div>
          ) : (
            <Text
              variant="header4"
              UNSAFE_className={`flex-1 ${textColor}`}
              useNewTextStyles
            >
              {slice.primary.sideText}
            </Text>
          )}
        </div>
        {!isListLinks && (
          <ListGridComponent items={slice.items} textColor={textColor} />
        )}
        {isListLinks && (
          <LinkListComponent items={slice.items} textColor={textColor} />
        )}
      </ContentContainer>
    </section>
  );
};

export default OurImpact;
